/**
 * Manejo centralizado de errores HTTP
 * Proporciona mensajes de error específicos basados en códigos de error
 * y en la respuesta del servidor
 */

export default function (response) {
	// Si no hay respuesta, retornar mensaje genérico
	if (!response) {
		return 'Error de conexión. Por favor verifique su conexión a internet.';
	}

	const { data, status } = response;

	// Si el servidor envía un mensaje de error, usarlo
	if (data && data.message) {
		return data.message;
	}

	// Si hay un código de error específico, manejarlo
	if (data && data.code) {
		const errorCode = data.code;

		// Errores de usuario
		if (errorCode === 'USER_EXISTS') {
			return 'El usuario ya existe en el sistema.';
		}
		if (errorCode === 'USER_NOT_FOUND') {
			return 'Usuario no encontrado.';
		}
		if (errorCode === 'INVALID_CREDENTIALS') {
			return 'Credenciales inválidas.';
		}

		// Errores de validación
		if (errorCode === 'VALIDATION_ERROR') {
			return data.error || 'Error de validación. Verifique los datos ingresados.';
		}
		if (errorCode === 'REQUIRED_FIELDS') {
			return 'Faltan campos requeridos. Por favor complete todos los campos obligatorios.';
		}

		// Errores de negocio
		if (errorCode === 'PROCESS_NOT_EDITABLE') {
			return 'El proceso no puede ser editado en su estado actual.';
		}
		if (errorCode === 'PROCESS_ALREADY_PUBLISHED') {
			return 'El proceso ya ha sido publicado y no puede ser modificado.';
		}
		if (errorCode === 'INVALID_DATE_RANGE') {
			return 'El rango de fechas no es válido.';
		}

		// Errores de permisos
		if (errorCode === 'INSUFFICIENT_PERMISSIONS') {
			return 'No tiene permisos suficientes para realizar esta acción.';
		}
		if (errorCode === 'RESOURCE_LOCKED') {
			return 'El recurso está siendo utilizado por otro usuario.';
		}
	}

	// Si hay errores de validación específicos en la respuesta
	if (data && data.errors && Array.isArray(data.errors)) {
		const firstError = data.errors[0];
		if (firstError.message) {
			return firstError.message;
		}
		if (firstError.field && firstError.msg) {
			return `Error en campo ${firstError.field}: ${firstError.msg}`;
		}
	}

	// Mensajes según el código de estado HTTP
	switch (status) {
	case 400:
		return 'Solicitud inválida. Verifique los datos enviados.';
	case 401:
		return 'Su sesión ha expirado. Por favor inicie sesión nuevamente.';
	case 403:
		return 'No tiene permisos para realizar esta acción.';
	case 404:
		return 'El recurso solicitado no existe.';
	case 409:
		return 'Conflicto de datos. El recurso ya existe o está en conflicto.';
	case 422:
		return 'Error de validación. Verifique los datos ingresados.';
	case 429:
		return 'Ha excedido el límite de solicitudes. Por favor espere unos minutos.';
	case 500:
		return 'Error interno del servidor. Por favor intente nuevamente.';
	case 502:
		return 'El servidor no está disponible. Por favor intente más tarde.';
	case 503:
		return 'Servicio no disponible. Por favor intente más tarde.';
	case 504:
		return 'Tiempo de espera agotado. Por favor intente nuevamente.';
	default:
		return 'Ocurrió un error inesperado. Por contacte al soporte técnico.';
	}
}

/**
 * Función auxiliar para extraer mensajes de error específicos
 * Útil cuando se necesita mostrar errores detallados en componentes
 *
 * @param {object} error - Error de axios
 * @returns {object} { message: string, details: any, status: number }
 */
export function parseError(error) {
	if (!error) {
		return {
			message: 'Error desconocido',
			details: null,
			status: null,
		};
	}

	const response = error.response || {};
	const data = response.data || {};
	const status = response.status || null;

	return {
		message: data.message || data.error || 'Error al procesar la solicitud',
		details: data.details || data.errors || null,
		status,
		code: data.code || null,
	};
}

/**
 * Función auxiliar para determinar si un error es de validación
 *
 * @param {object} error - Error de axios
 * @returns {boolean}
 */
export function isValidationError(error) {
	const status = error.response && error.response.status;
	const code = error.response && error.response.data && error.response.data.code;
	return status === 422 || status === 400 || code === 'VALIDATION_ERROR';
}

/**
 * Función auxiliar para determinar si un error es de red/conexión
 *
 * @param {object} error - Error de axios
 * @returns {boolean}
 */
export function isNetworkError(error) {
	return !error.response && !!error.message;
}



// WEBPACK FOOTER //
// ./src/http/errors.js